import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';

import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Stack,
  useTheme,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Fade,
  Grow,
  TableSortLabel,
  Tooltip,
  Badge,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';

import { alpha } from '@mui/material/styles';
import { styled, keyframes } from '@mui/material/styles';
import AdminNavbar from '../../components/Navbar/AdminNavbar';
import Footer from '../../components/Footer/Footer';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import LockResetIcon from '@mui/icons-material/LockReset';

// Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { authen } from '../../firebase/firebase';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

// Utils
import RemoteErrorLog from '../../Utils/api';
import axios from 'axios';
import { environment } from '../../../environments/environment';

interface Student {
  id: string;
  username?: string;
  email?: string;
  school_info?: {
    school_id?: string;
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  type?: string;
  auth_type?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  created_at?: any;
  [key: string]: any;
}

type SortField = 'username' | 'type';
type SortDirection = 'asc' | 'desc';

// Brand colors
const BRAND_PRIMARY = '#2A9235';
const BRAND_SECONDARY = '#4CAF50';

// Animations
const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const slideInFromLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-30px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromBottom = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: `
    linear-gradient(135deg, #f8fafc 0%, #f1f5f9 30%, #e2e8f0 100%),
    radial-gradient(ellipse at 20% 80%, rgba(42, 146, 53, 0.03) 0%, transparent 60%),
    radial-gradient(ellipse at 80% 20%, rgba(76, 175, 80, 0.02) 0%, transparent 60%)
  `,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      repeating-linear-gradient(
        45deg,
        transparent,
        transparent 60px,
        rgba(42, 146, 53, 0.005) 62px
      )
    `,
    pointerEvents: 'none',
    zIndex: -1,
  },
});

const MainContent = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4), // Added more space from navbar
  paddingBottom: theme.spacing(2),
  maxHeight: 'calc(100vh - 140px)',
  display: 'flex',
  flexDirection: 'column',
}));

// School Name Card (simplified without "User Directory")
const SchoolNameCard = styled(Card)(({ theme }) => ({
  background: `
    linear-gradient(135deg,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(248, 252, 249, 0.9) 50%,
      rgba(240, 248, 241, 0.85) 100%
    )
  `,
  backdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(42, 146, 53, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  position: 'relative',
  marginBottom: theme.spacing(1.5),
  animation: `${slideInFromLeft} 0.8s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${BRAND_PRIMARY} 0%, ${BRAND_SECONDARY} 50%, ${BRAND_PRIMARY} 100%)`,
    backgroundSize: '200% 100%',
    animation: `${gradientShift} 4s ease infinite`,
  },
}));

const TableSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
}));

const TableCard = styled(Card)(({ theme }) => ({
  background: `
    linear-gradient(135deg,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(248, 252, 249, 0.9) 100%
    )
  `,
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(42, 146, 53, 0.1)',
  borderRadius: 16,
  overflow: 'hidden',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: `
    0 20px 40px rgba(42, 146, 53, 0.08),
    0 8px 16px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.8)
  `,
  animation: `${slideInFromBottom} 1s ease-out`,
}));

// Table Header with Search
const TableHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2.5),
  background: `
    linear-gradient(135deg,
      rgba(42, 146, 53, 0.08) 0%,
      rgba(76, 175, 80, 0.06) 50%,
      rgba(42, 146, 53, 0.04) 100%
    )
  `,
  borderBottom: '1px solid rgba(42, 146, 53, 0.12)',
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    background: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: '0.875rem',
    height: 44,
    '& fieldset': {
      border: '1px solid rgba(42, 146, 53, 0.15)',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      background: 'rgba(255, 255, 255, 1)',
      '& fieldset': {
        borderColor: 'rgba(42, 146, 53, 0.3)',
      },
    },
    '&.Mui-focused': {
      background: 'rgba(255, 255, 255, 1)',
      '& fieldset': {
        borderColor: BRAND_PRIMARY,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    fontWeight: 500,
    '&.Mui-focused': {
      color: BRAND_PRIMARY,
      fontWeight: 600,
    },
  },
}));

// Responsive Table Container
const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: 6,
    height: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(BRAND_PRIMARY, 0.05),
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(BRAND_PRIMARY, 0.3),
    borderRadius: 3,
    '&:hover': {
      background: alpha(BRAND_PRIMARY, 0.5),
    },
  },
}));

const ModernTable = styled(Table)(({ theme }) => ({
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableHead-root': {
    background: 'transparent',
  },
  '& .MuiTableCell-head': {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: BRAND_PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: theme.spacing(1.5, 2),
    borderBottom: `2px solid rgba(42, 146, 53, 0.12)`,
    '&:first-of-type': { width: '60px' },
    '&:nth-of-type(2)': { width: 'auto' },
    '&:last-of-type': { width: '120px' },
  },
  '& .MuiTableRow-root': {
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:nth-of-type(even)': {
      background: 'rgba(42, 146, 53, 0.015)',
    },
    '&:hover': {
      background: `linear-gradient(90deg,
        rgba(42, 146, 53, 0.04) 0%,
        rgba(76, 175, 80, 0.02) 50%,
        rgba(42, 146, 53, 0.04) 100%
      )`,
      transform: 'translateX(2px)',
      boxShadow: 'inset 3px 0 0 rgba(42, 146, 53, 0.3)',
    },
  },
  '& .MuiTableCell-body': {
    fontSize: '0.875rem',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid rgba(42, 146, 53, 0.04)',
    fontWeight: 500,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.user-info-cell': {
    minWidth: 0,
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '200px',
    },
  },
  '&.type-cell': {
    textAlign: 'left',
    paddingRight: theme.spacing(2),
  },
}));

const EmptyStateCard = styled(Card)(({ theme }) => ({
  background: `
    radial-gradient(ellipse at center,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(248, 252, 249, 0.9) 50%,
      rgba(240, 248, 241, 0.85) 100%
    )
  `,
  backdropFilter: 'blur(20px)',
  borderRadius: 20,
  border: '1px solid rgba(42, 146, 53, 0.12)',
  textAlign: 'center',
  padding: theme.spacing(5, 3),
  animation: `${slideInFromBottom} 0.8s ease-out`,
}));

const LoadingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
  minHeight: 300,
  background: `
    radial-gradient(circle at center,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(248, 252, 249, 0.6) 100%
    )
  `,
  borderRadius: 16,
}));

const AdminSchoolUsers: React.FC = () => {
  const theme = useTheme();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState('');
  const [adminSchoolId, setAdminSchoolId] = useState('');

  // Filtering and sorting states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('username');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Reset password states
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Footer visibility based on PAGE scroll (not table scroll)
  const [showFooter, setShowFooter] = useState(false);

  // Responsive table height
  const tableSectionRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableMaxHeight, setTableMaxHeight] = useState('calc(100vh - 350px)');

  // Compute table max height
  const computeTableMaxHeight = useCallback(() => {
    try {
      if (!tableSectionRef.current) return;
      const rect = tableSectionRef.current.getBoundingClientRect();
      const top = rect.top;
      const gap = 4;
      const bottomPadding = 16;
      const available = Math.max(
        window.innerHeight - top - gap - bottomPadding,
        200
      );
      setTableMaxHeight(`${Math.floor(available)}px`);
    } catch (e) {
      setTableMaxHeight('calc(100vh - 350px)');
    }
  }, []);

  useLayoutEffect(() => {
    computeTableMaxHeight();
    const onResize = () => computeTableMaxHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [students, computeTableMaxHeight]);

  // PAGE scroll logic for footer (not table scroll)
  useEffect(() => {
    const onPageScroll = () => {
      // Only listen to window/page scroll, not table scroll
      setShowFooter(window.scrollY > 100);
    };

    onPageScroll(); // Initialize
    window.addEventListener('scroll', onPageScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onPageScroll);
    };
  }, []);

  useEffect(() => {
    const db = getFirestore();
    const unsub = onAuthStateChanged(authen, async (user) => {
      setLoading(true);
      setError(null);
      if (!user) {
        setStudents([]);
        setLoading(false);
        return;
      }

      try {
        let caData: any | null = null;
        const caRef = doc(db, 'custom_auth', user.uid);
        const caSnap = await getDoc(caRef);
        if (caSnap.exists()) {
          caData = caSnap.data();
        } else if (user.email) {
          const q = query(
            collection(db, 'custom_auth'),
            where('email', '==', user.email)
          );
          const qs = await getDocs(q);
          if (!qs.empty) caData = qs.docs[0].data();
        }

        if (!caData) {
          setError('Admin account not found in database.');
          setStudents([]);
          setLoading(false);
          return;
        }

        const schoolId =
          caData?.school_info?.school_id ||
          caData?.school_id ||
          caData?.schoolId ||
          caData?.schoolID ||
          null;

        if (!schoolId) {
          setError('No school associated with the logged-in admin.');
          setStudents([]);
          setLoading(false);
          return;
        }

        setAdminSchoolId(schoolId);

        if (caData?.type && caData.type === 'student') {
          setError(
            'Current account is a student; unable to list students for this school.'
          );
          setStudents([]);
          setLoading(false);
          return;
        }

        if (caData?.school_info?.name) {
          setSchoolName(caData.school_info.name);
        }

        const studentsQuery = query(
          collection(db, 'custom_auth'),
          where('type', 'in', ['student', 'teacher']),
          where('school_info.school_id', '==', String(schoolId))
        );

        const snap = await getDocs(studentsQuery);
        const items: Student[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        }));

        setStudents(items);
      } catch (e: any) {
        console.error('Error loading students:', e);
        RemoteErrorLog(e, 'AdminSchoolStudent.tsx');
        setError('Failed to load students.');
        setStudents([]);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort students
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = students.filter((student) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        student.username?.toLowerCase().includes(searchLower) ||
        student.full_name?.toLowerCase().includes(searchLower) ||
        student.first_name?.toLowerCase().includes(searchLower) ||
        student.last_name?.toLowerCase().includes(searchLower) ||
        student.type?.toLowerCase().includes(searchLower)
      );
    });

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'username':
          aValue = a.username || a.id || '';
          bValue = b.username || b.id || '';
          break;
        case 'type':
          aValue = a.type || 'student';
          bValue = b.type || 'student';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [students, searchTerm, sortField, sortDirection]);

  // Calculate stats
  const studentCount = students.filter((s) => s.type === 'student').length;
  const teacherCount = students.filter((s) => s.type === 'teacher').length;

  // Reset password functions
  const handleResetPasswordClick = (student: Student) => {
    setSelectedStudent(student);
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
    setResetPasswordOpen(true);
  };

  const handleResetPasswordClose = () => {
    setResetPasswordOpen(false);
    setSelectedStudent(null);
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
  };

  const handleResetPasswordSubmit = async () => {
    if (!selectedStudent) return;

    // Validation
    if (!newPassword || newPassword.length < 6) {
      setResetError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    setResetLoading(true);
    setResetError(null);

    try {
      const currentUser = authen.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      
      const response = await axios.post(
        `${environment.backendUrl}api/student/reset-password`,
        {
          uid: currentUser.uid,
          studentId: selectedStudent.id,
          newPassword: newPassword,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        setResetSuccess(true);
        handleResetPasswordClose();
      } else {
        throw new Error(response.data?.error || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      setResetError(
        error.response?.data?.error || 
        error.message || 
        'Failed to reset password. Please try again.'
      );
      RemoteErrorLog(error, 'AdminSchoolUsers.tsx - resetPassword');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetSuccessClose = () => {
    setResetSuccess(false);
  };

  return (
    <>
      <PageWrapper>
        <AdminNavbar />
        <MainContent maxWidth="xl">
          {loading ? (
            <LoadingWrapper>
              <CircularProgress size={40} sx={{ color: BRAND_PRIMARY }} />
            </LoadingWrapper>
          ) : error ? (
            <EmptyStateCard>
              <SchoolIcon
                sx={{ fontSize: 64, color: alpha(BRAND_PRIMARY, 0.3), mb: 2 }}
              />
              <Typography variant="h6" color="error" gutterBottom>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please contact support if this issue persists.
              </Typography>
            </EmptyStateCard>
          ) : (
            <>
              {/* School Name + Stats Row */}
              <SchoolNameCard>
                <CardContent sx={{ py: 2, position: 'relative', zIndex: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={3}
                  >
                    {/* Left side - Only School Name */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(BRAND_PRIMARY, 0.12),
                          color: BRAND_PRIMARY,
                          width: 42,
                          height: 42,
                          border: `2px solid ${alpha(BRAND_PRIMARY, 0.2)}`,
                        }}
                      >
                        <GroupsIcon sx={{ fontSize: 24 }} />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_SECONDARY} 100%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                            letterSpacing: '-0.01em',
                          }}
                        >
                          {schoolName || 'School Dashboard'}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Right side - Stats in a horizontal row */}
                    <Stack direction="row" spacing={4} alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(BRAND_PRIMARY, 0.12),
                            color: BRAND_PRIMARY,
                            width: 32,
                            height: 32,
                          }}
                        >
                          <GroupsIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: BRAND_PRIMARY,
                              lineHeight: 1.2,
                            }}
                          >
                            {filteredAndSortedStudents.length}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, lineHeight: 1 }}
                          >
                            Total Users
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(BRAND_PRIMARY, 0.12),
                            color: BRAND_PRIMARY,
                            width: 32,
                            height: 32,
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: BRAND_PRIMARY,
                              lineHeight: 1.2,
                            }}
                          >
                            {studentCount}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, lineHeight: 1 }}
                          >
                            Students
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            bgcolor: alpha('#FF9800', 0.12),
                            color: '#FF9800',
                            width: 32,
                            height: 32,
                          }}
                        >
                          <SupervisorAccountIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: '#FF9800',
                              lineHeight: 1.2,
                            }}
                          >
                            {teacherCount}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, lineHeight: 1 }}
                          >
                            Teachers
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </SchoolNameCard>

              {/* Users Table with Integrated Search */}
              <TableSection ref={tableSectionRef}>
                {filteredAndSortedStudents.length === 0 ? (
                  <EmptyStateCard>
                    <SchoolIcon
                      sx={{
                        fontSize: 64,
                        color: alpha(BRAND_PRIMARY, 0.3),
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: BRAND_PRIMARY, fontWeight: 700 }}
                    >
                      {searchTerm ? 'No Users Found' : 'No Users Yet'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      {searchTerm
                        ? `No users match "${searchTerm}". Try adjusting your search criteria.`
                        : 'No users found for your school. Generate user accounts from the dashboard to get started.'}
                    </Typography>
                    {searchTerm && (
                      <IconButton
                        onClick={() => setSearchTerm('')}
                        sx={{
                          bgcolor: alpha(BRAND_PRIMARY, 0.1),
                          color: BRAND_PRIMARY,
                          '&:hover': {
                            bgcolor: alpha(BRAND_PRIMARY, 0.2),
                          },
                        }}
                      >
                        <FilterListIcon />
                      </IconButton>
                    )}
                  </EmptyStateCard>
                ) : (
                  <TableCard>
                    {/* Table Header with Search */}
                    <TableHeader>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: BRAND_PRIMARY,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <GroupsIcon sx={{ fontSize: 20 }} />
                        Users ({filteredAndSortedStudents.length})
                      </Typography>

                      <ModernTextField
                        placeholder="Search users..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: 300 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  color: alpha(BRAND_PRIMARY, 0.5),
                                  fontSize: 20,
                                }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: searchTerm && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setSearchTerm('')}
                                sx={{
                                  color: alpha(BRAND_PRIMARY, 0.7),
                                  '&:hover': {
                                    bgcolor: alpha(BRAND_PRIMARY, 0.08),
                                  },
                                }}
                              >
                                <FilterListIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableHeader>

                    <ResponsiveTableContainer
                      ref={tableContainerRef}
                      sx={{
                        maxHeight: tableMaxHeight,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        width: '100%',
                      }}
                    >
                      <ModernTable>
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortField === 'username'}
                                direction={
                                  sortField === 'username'
                                    ? sortDirection
                                    : 'asc'
                                }
                                onClick={() => handleSort('username')}
                                sx={{
                                  '&.MuiTableSortLabel-active': {
                                    color: `${BRAND_PRIMARY} !important`,
                                    fontWeight: 800,
                                  },
                                  '&.MuiTableSortLabel-active .MuiTableSortLabel-icon':
                                    {
                                      color: `${BRAND_PRIMARY} !important`,
                                    },
                                }}
                              >
                                User Information
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortField === 'type'}
                                direction={
                                  sortField === 'type' ? sortDirection : 'asc'
                                }
                                onClick={() => handleSort('type')}
                                sx={{
                                  '&.MuiTableSortLabel-active': {
                                    color: `${BRAND_PRIMARY} !important`,
                                    fontWeight: 800,
                                  },
                                  '&.MuiTableSortLabel-active .MuiTableSortLabel-icon':
                                    {
                                      color: `${BRAND_PRIMARY} !important`,
                                    },
                                }}
                              >
                                Account Type
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '80px', textAlign: 'center' }}>
                              Reset Password
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredAndSortedStudents.map((student, idx) => (
                            <TableRow key={student.id}>
                              <StyledTableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: alpha(BRAND_PRIMARY, 0.7),
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {String(idx + 1).padStart(2, '0')}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell className="user-info-cell">
                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="center"
                                >
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: alpha(BRAND_PRIMARY, 0.12),
                                      color: BRAND_PRIMARY,
                                      fontWeight: 700,
                                      fontSize: '0.85rem',
                                    }}
                                  >
                                    {(student.username || student.id)
                                      .charAt(0)
                                      .toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: BRAND_PRIMARY,
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {student.username || student.id}
                                    </Typography>
                                    {student.full_name && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {student.full_name}
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>
                              </StyledTableCell>
                              <StyledTableCell className="type-cell">
                                <Chip
                                  icon={
                                    student.type === 'teacher' ? (
                                      <SupervisorAccountIcon />
                                    ) : (
                                      <PersonIcon />
                                    )
                                  }
                                  label={student.type || 'Student'}
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      student.type === 'teacher'
                                        ? alpha('#FF9800', 0.12)
                                        : alpha(BRAND_PRIMARY, 0.12),
                                    color:
                                      student.type === 'teacher'
                                        ? '#FF9800'
                                        : BRAND_PRIMARY,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    textTransform: 'capitalize',
                                    borderRadius: 2,
                                    height: 26,
                                    border:
                                      student.type === 'teacher'
                                        ? '1px solid rgba(255, 152, 0, 0.3)'
                                        : `1px solid ${alpha(
                                            BRAND_PRIMARY,
                                            0.3
                                          )}`,
                                    '& .MuiChip-icon': {
                                      fontSize: '0.85rem',
                                    },
                                  }}
                                />
                              </StyledTableCell>
                              <StyledTableCell sx={{ textAlign: 'center' }}>
                                <Tooltip title="Reset Password" arrow>
                                  <IconButton
                                    onClick={() => handleResetPasswordClick(student)}
                                    size="small"
                                    sx={{
                                      color: alpha(BRAND_PRIMARY, 0.7),
                                      '&:hover': {
                                        color: BRAND_PRIMARY,
                                        bgcolor: alpha(BRAND_PRIMARY, 0.08),
                                      },
                                    }}
                                  >
                                    <LockResetIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </StyledTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </ModernTable>
                    </ResponsiveTableContainer>
                  </TableCard>
                )}
              </TableSection>
            </>
          )}
        </MainContent>
      </PageWrapper>
      <Footer />

      {/* Reset Password Modal */}
      <Dialog
        open={resetPasswordOpen}
        onClose={handleResetPasswordClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `
              linear-gradient(135deg,
                rgba(255, 255, 255, 0.95) 0%,
                rgba(248, 252, 249, 0.9) 100%
              )
            `,
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(42, 146, 53, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: BRAND_PRIMARY,
            fontWeight: 700,
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid rgba(42, 146, 53, 0.12)',
            pb: 2,
          }}
        >
          <LockResetIcon />
          Reset Password
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedStudent && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Reset password for:
              </Typography>
              <Typography variant="h6" sx={{ color: BRAND_PRIMARY, fontWeight: 600 }}>
                {selectedStudent.username || selectedStudent.id}
              </Typography>
              {selectedStudent.full_name && (
                <Typography variant="body2" color="text.secondary">
                  {selectedStudent.full_name}
                </Typography>
              )}
            </Box>
          )}

          {resetError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {resetError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Password must be at least 6 characters long"
            InputProps={{
              sx: {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: alpha(BRAND_PRIMARY, 0.2),
                },
                '&:hover fieldset': {
                  borderColor: alpha(BRAND_PRIMARY, 0.4),
                },
                '&.Mui-focused fieldset': {
                  borderColor: BRAND_PRIMARY,
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              sx: {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: alpha(BRAND_PRIMARY, 0.2),
                },
                '&:hover fieldset': {
                  borderColor: alpha(BRAND_PRIMARY, 0.4),
                },
                '&.Mui-focused fieldset': {
                  borderColor: BRAND_PRIMARY,
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleResetPasswordClose}
            disabled={resetLoading}
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetPasswordSubmit}
            disabled={resetLoading || !newPassword || !confirmPassword}
            variant="contained"
            sx={{
              bgcolor: BRAND_PRIMARY,
              color: 'white',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                bgcolor: BRAND_SECONDARY,
              },
              '&:disabled': {
                bgcolor: alpha(BRAND_PRIMARY, 0.3),
                color: 'white',
              },
            }}
          >
            {resetLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Reset Password'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <Snackbar
        open={resetSuccess}
        autoHideDuration={6000}
        onClose={handleResetSuccessClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleResetSuccessClose}
          severity="success"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          Password reset successfully!
        </Alert>
      </Snackbar>
    </>
  );
};
export default AdminSchoolUsers;
